import fs from 'fs';
import http from 'http';
import https from 'https';
import express from 'express';
import cors from 'cors';
import pify from 'pify';
import shutdown from 'http-shutdown';
import Service from './lib/service';
import Zone from './lib/zone';
import { render } from './lib/util';

class Plaqard {
	/**
	 * @param  {object} options
	 * @param  {object[]} options.zones
	 * @param  {Service} options.service
	 * @param  {string} options.hostname
	 * @param  {number} options.port
	 * @param  {string} options.sslKey
	 * @param  {string} options.sslCert
	 */
	constructor(options = {}) {
		const {
			zones = [],
			service = null,
			hostname = 'localhost',
			port = 9000,
			sslKey = null,
			sslCert = null
		} = options;

		if (service instanceof Service === false) {
			throw new TypeError('Expected a service.');
		}

		const zonesInstances = zones.map(
			({ id, dimensions, contentPath, contentType: ZoneType }) => {
				if (typeof ZoneType === 'undefined') {
					throw new TypeError(`Expected a zone for "${id}".`);
				}
				return new ZoneType(id, dimensions, contentPath);
			}
		);

		this.service = service;
		this.zones = zonesInstances;
		this.hostname = hostname;
		this.port = port;
		this.isSecure = (Boolean(sslKey) && Boolean(sslCert)) === true;
	}

	/**
	 * @returns {Promise<object>}
	 */
	async start() {
		const server = express();

		server.use([
			cors({
				origin: true,
				credentials: true
			})
		]);

		const zones = this.zones.map((zone) => ({
			id: zone.id,
			render: zone.render.bind(zone)
		}));

		const app = this.service.setupServer({
			app: server,
			zones: zones,
			hostname: this.hostname,
			port: this.port,
			isSecure: this.isSecure,
			render: render
		});

		/* istanbul ignore if */
		if (this.isSecure) {
			const [key, cert] = await Promise.all([
				pify(fs.readFile)(this.sslKey, 'utf8'),
				pify(fs.readFile)(this.sslCert, 'utf8')
			]);

			this.server = shutdown(https.createServer({ key, cert }, app));
		} else {
			this.server = shutdown(http.createServer(app));
		}

		return new Promise((resolve, reject) => {
			this.server
				.listen(this.port, this.hostname, () => {
					resolve({
						port: this.port,
						hostname: this.hostname
					});
				})
				.on('error', (error) => {
					reject(error);
				});
		});
	}

	/**
	 * @returns {Promise<*>}
	 */
	async stop() {
		if (typeof this.server === 'undefined') {
			return;
		}
		return new Promise((resolve, reject) => {
			this.server.shutdown((error) => {
				/* istanbul ignore if */
				if (error) {
					reject(error);
					return;
				}
				resolve();
			});
		});
	}
}

/**
 * @param  {object} options
 *
 * @returns {object}
 */
export default (options = {}) => {
	const instance = new Plaqard(options);

	return {
		start: instance.start.bind(instance),
		stop: instance.stop.bind(instance)
	};
};

export { Service, Zone };
